import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { ordersApi, type CreateOrderPayload, type OrderListQuery, type UpdateStatusPayload, type Order } from '../api/orders.api'
import { qk } from '@/lib/api/queryKeys'

export function useCreateOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: CreateOrderPayload) => ordersApi.create(p),
    onSuccess: (order) => {
      qc.setQueryData<Order>(qk.orders.one(order.id), order)
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['cart'] })
    },
  })
}

export function useMyOrders(q: OrderListQuery = {}) {
  return useQuery({
    queryKey: qk.orders.mine(q),
    queryFn: () => ordersApi.mine(q),
    placeholderData: keepPreviousData,
  })
}

export function useOrderDetail(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.orders.one(id) : ['orders', 'one', 'noop'],
    queryFn: () => ordersApi.one(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

// Admin
export function useAdminOrders(q: OrderListQuery = {}) {
  return useQuery({
    queryKey: qk.orders.all(q),
    queryFn: () => ordersApi.adminList(q),
    placeholderData: keepPreviousData,
  })
}

export function useAdminOrderDetail(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.orders.one(id) : ['orders', 'one', 'noop'],
    queryFn: () => ordersApi.adminOne(id!),
    enabled: !!id,
  })
}

export function useAdminUpdateOrderStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { id: string } & UpdateStatusPayload) =>
      ordersApi.adminStatus(p.id, { status: p.status, note: p.note }),
    onSuccess: (order) => {
      qc.setQueryData<Order>(qk.orders.one(order.id), order)
      qc.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}
